import { X, CheckCircle, AlertTriangle, CheckCheck, Gavel } from 'lucide-react';
import { useEffect, useState } from 'react';
import PoLineLink from '../../common/PoLineLink';
import PoHeaderLink from '../../common/PoHeaderLink';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { purchasingAPI, GoodsReceiptNote, DeliveryAppeal, POChangeLog } from '../../../lib/api';
import type { DeliveryAppealLog, DeliveryAppealAttachment } from '../../../lib/api/types/grn-types';
import { CUSTOMER_REQUIRED_DATE, ORIGINAL_REQUIRED_DATE } from '../../../config/dateLabels';
import { AcknowledgeGRNModal, DisputeGRNModal, ResolveGRNDisputeModal, type GRNWorkflowData } from '../GRNWorkflowModals';
import { useFormatters } from '../../../contexts/PreferencesContext';
import { useAuth } from '../../../contexts/AuthContext';
import { useMenuSettings } from '../../../contexts/MenuSettingsContext';
import { getAppealStatusBadge, getOntimeStatusBadge } from '../tabs/delivery/deliveryBadges';
import { getApiErrorMessage } from '../../../utils/errorUtils';
import { toast } from 'sonner';
import RaiseAppealModal from './RaiseAppealModal';
import AppealResponseModal from './AppealResponseModal';
import GRNScoreChart from './GRNScoreChart';
import { getVarianceBadge } from '../tabs/delivery/deliveryBadges';
import { isQuantityOnlyAppeal } from '../tabs/delivery/appealEligibility';
import { useColumnSort } from '../../../hooks/useColumnSort';
import { SortableTh } from '../../common/SortableTh';
import {
  cleanWorkflowNote, describeWorkflowAction, WORKFLOW_SORT_KEYS, APPEAL_SORT_KEYS,
} from './deliveryAuditSort';
import { deliveryHeaderStatus } from './deliveryHeaderStatus';

interface DeliveryDetailsModalProps {
  grn: GoodsReceiptNote;
  /** When a single grn_number covers multiple PO lines, the parent passes
   *  every sister GRN record here. The modal then aggregates qty and
   *  merges schedule allocations across all lines in the Allocations tab. */
  grnGroup?: GoodsReceiptNote[];
  onClose: () => void;
  onRefresh?: () => void;
}

export default function DeliveryDetailsModal({ grn, onClose, onRefresh }: DeliveryDetailsModalProps) {
  const { user } = useAuth();
  const { isOpenOrdersControlHidden, isFeatureEnabled } = useMenuSettings();
  const isSupplier = user?.platform === 'SUPPLIER';
  // Schedule-grain tenants: a GRN maps to one schedule line, so Qty Ordered is
  // the attributed schedule's qty (matching the Deliveries grid), not the PO line.
  const scheduleCollabActive = isFeatureEnabled('po_line_schedules');
  // CR-5884: Open Orders supplier-side hide-toggles surface in this detail
  // modal too — the GRN delivery trend chart and the OTIF override (Appeal)
  // controls live here. Customer / Helix viewers always see them.
  const hideTrendLineForSupplier = isOpenOrdersControlHidden('hide_trend_line', isSupplier);
  const hideAppealForSupplier = isOpenOrdersControlHidden('hide_otif_override_button', isSupplier);
  const [showAcknowledgeModal, setShowAcknowledgeModal] = useState(false);
  const [showDisputeModal, setShowDisputeModal] = useState(false);
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [showAppealModal, setShowAppealModal] = useState(false);
  const [showAppealResponseModal, setShowAppealResponseModal] = useState(false);
  const [grnData, setGrnData] = useState(grn);
  const [latestAppeal, setLatestAppeal] = useState<DeliveryAppeal | null>(null);
  const [appealLogs, setAppealLogs] = useState<DeliveryAppealLog[]>([]);
  const [appealAttachments, setAppealAttachments] = useState<DeliveryAppealAttachment[]>([]);
  // CR-4601 rework: GRN workflow audit trail (acknowledge / dispute / resolve events)
  const [workflowLogs, setWorkflowLogs] = useState<POChangeLog[]>([]);
  // CR-7263: the two audit tables below. Both are the full log for one
  // delivery, unpaginated, so sorting the loaded array is complete. Note
  // R-056: the change-log endpoint silently ignores ?ordering= on this
  // branch, so a UI control must never be wired to it -- client-side is not
  // a shortcut here, it is the only thing that works.
  const workflowSort = useColumnSort(workflowLogs, { dateFields: ['change_date'], computed: WORKFLOW_SORT_KEYS });
  const appealSort = useColumnSort(appealLogs, { dateFields: ['performed_at'], computed: APPEAL_SORT_KEYS });

  const [loading, setLoading] = useState(false);
  // CR-4869 round 3: Action clusters were gated on `!loading`, but `loading`
  // starts false — first paint happened before the fetch even began, so the
  // pop-up briefly rendered Acknowledge before swapping to Cancel Appeal once
  // latestAppeal arrived. Track initial-load completion separately so action
  // clusters stay suppressed until the appeal lookup has resolved at least
  // once.
  const [initialLoaded, setInitialLoaded] = useState(false);

  const companyType = user?.company_type;


  // Fetch full GRN details on mount to get workflow status
  useEffect(() => {
    if (grn.id) {
      fetchGRNDetails();
    }
  }, [grn.id]);

  const fetchGRNDetails = async () => {
    try {
      const response = await purchasingAPI.getGRN(grn.id);
      setGrnData(response.data);

      // CR-4601 rework: Fetch the workflow-status audit trail for this GRN so
      // the popup can show who acknowledged/disputed/resolved it and when.
      try {
        const logsResponse = await purchasingAPI.getChangelogs({
          grn: grn.id,
          field_changed: 'workflow_status',
          ordering: 'change_date',
        });
        setWorkflowLogs(logsResponse.data.results || []);
      } catch (err: unknown) {
        console.error('Failed to fetch GRN workflow audit trail:', err);
        setWorkflowLogs([]);
      }

      // If GRN has a latest appeal, fetch its details
      // CR-4678 round 5: Previously a cancelled appeal was wiped to null
      // here ("treat as no active appeal"), which also nuked the Appeal
      // History audit trail — Sarah's bounce: "the audit trail has
      // disappeared which needs to be re-initiated to show that an appeal
      // was submitted and then cancelled". Keep the appeal + logs visible
      // so the cancellation is still part of the visible record. The
      // action-button gating below uses status checks
      // (awaiting_response/queried for active, rejected/accepted for
      // resolved) and naturally treats 'cancelled' as not-active, so
      // Acknowledge + re-Appeal still surface for the re-appeal flow.
      if (response.data.latest_appeal?.id) {
        try {
          const appealResponse = await purchasingAPI.getDeliveryAppeal(response.data.latest_appeal.id);
          setLatestAppeal(appealResponse.data);
          try {
            const [logsResponse, attachmentsResponse] = await Promise.all([
              purchasingAPI.getDeliveryAppealLogs(response.data.latest_appeal.id),
              purchasingAPI.getAppealAttachments(response.data.latest_appeal.id),
            ]);
            setAppealLogs(logsResponse.data);
            setAppealAttachments(attachmentsResponse.data);
          } catch { /* ignore log/attachment fetch failure */ }
        } catch (err: unknown) {
          console.error('Failed to fetch appeal details:', err);
        }
      } else {
        // CR-4617: No appeal recorded at all — clear stale state.
        setLatestAppeal(null);
        setAppealLogs([]);
        setAppealAttachments([]);
      }
    } catch (error) {
      console.error('Failed to fetch GRN details:', error);
    } finally {
      // CR-4869 round 3: mark initial load complete (success or error) so the
      // action clusters can render. Using try/finally guarantees we don't
      // leave the user staring at an empty footer if the appeal lookup fails.
      setInitialLoaded(true);
    }
  };

  // Close on ESC key
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  const { formatDate: contextFormatDate, formatQuantity } = useFormatters();

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return contextFormatDate(dateString);
  };

  // CR-7418 (Aaron, 19 Sep 2026): *"mismatch of colours here one grid label is
  // green, modal label is grey"*. This modal held its OWN copy of the on-time
  // badge, and the copy predated CR-6518's native overridden verdicts — so
  // `overridden_late` missed its map and fell through to the grey default,
  // while the Deliveries grid two inches behind it rendered the same delivery
  // green from the shared function. Deleted; it imports the one definition,
  // which is what P2 asks for when the same predicate exists twice.

  // CR-4603: Directional status labels
  // CR-7418 round 9: the SECOND private copy of a shared badge in this one
  // file, found by the platform-wide badge sweep and drifted exactly like the
  // first. The shared getAppealStatusBadge draws ring-style chips and gives an
  // action-required state a pulsing dot; this copy drew flat YELLOW chips with
  // no dot, so an appeal awaiting a response pulsed amber in the Deliveries
  // grid and sat inert in the modal opened from that same row. Deleted; it
  // imports the one definition, which also brings the blank handling the copy
  // never had.

  const handleWorkflowSubmit = async (data: GRNWorkflowData) => {
    setLoading(true);
    try {
      if (data.action === 'acknowledge') {
        await purchasingAPI.acknowledgeGRN(data.grnId, {
          comments: data.comments,
        });
      } else if (data.action === 'dispute') {
        await purchasingAPI.disputeGRN(data.grnId, {
          reason_id: data.reason_id as number,
          comments: data.comments,
        });
      } else if (data.action === 'resolve') {
        await purchasingAPI.resolveGRNDispute(data.grnId, {
          comments: data.comments,
        });
      }

      // Refresh GRN details
      await fetchGRNDetails();

      // Refresh parent list if callback provided
      if (onRefresh) {
        onRefresh();
      }
    } catch (error: unknown) {
      console.error('Workflow action failed:', error);
      toast.error(getApiErrorMessage(error, 'Failed to process workflow action'));
    } finally {
      setLoading(false);
    }
  };

  const handleAppealSuccess = async () => {
    // CR-4617: Clear stale appeal state before refreshing
    setLatestAppeal(null);
    setAppealLogs([]);
    setAppealAttachments([]);
    await fetchGRNDetails();
    if (onRefresh) {
      onRefresh();
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-40"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b shrink-0">
            <div className="flex items-center gap-3">
              <h2 className="text-xl font-bold">Delivery Details</h2>
              {/* CR-4704: the GRN's workflow state; EXPEDITING-3: a delivery decided
                  in OTIF review shows that decision instead of "Pending Review". */}
              {(() => {
                const chip = deliveryHeaderStatus(grnData);
                if (!chip) return null;
                return (
                  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${chip.className}`}>
                    {chip.tone === 'done' && <CheckCircle className="h-3 w-3" />}
                    {chip.tone === 'warning' && <AlertTriangle className="h-3 w-3" />}
                    {chip.label}
                  </span>
                );
              })()}
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Scrollable Content */}
          <div className="overflow-y-auto flex-1 min-h-0">
          <Tabs defaultValue="details" className="w-full">
            <TabsList className="mx-6 mt-4">
              <TabsTrigger value="details">Details</TabsTrigger>
            </TabsList>
            <TabsContent value="details" className="mt-0">
          {/* Content - 2 Column Layout */}
          <div className="p-6">
            <div className="grid grid-cols-2 gap-x-12 gap-y-6">
              {/* Left Column */}
              <div className="space-y-6">
                <div>
                  <label className="text-sm font-medium text-gray-500">GRN Number</label>
                  <p className="mt-1 text-base font-medium">{grnData.grn_number}</p>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-500">Customer</label>
                  <p className="mt-1 text-base">{grnData.customer_name || grnData.po_line_details?.po_details?.customer_details?.name || '-'}</p>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-500">Supplier Group</label>
                  <p className="mt-1 text-base">{grnData.supplier_group || '-'}</p>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-500">Supplier Site</label>
                  <p className="mt-1 text-base">{grnData.supplier_name || grnData.po_line_details?.po_details?.supplier_details?.name || '-'}</p>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-500">PO Number</label>
                  <p className="mt-1 text-base font-medium">
                    {/* CR-1154: PO number links to the PO header; line links to the line. */}
                    <PoHeaderLink poLineId={grnData.po_line} poNumber={grnData.po_number || grnData.po_line_details?.po_details?.po_number || undefined}>
                      {grnData.po_number || grnData.po_line_details?.po_details?.po_number || '-'}
                    </PoHeaderLink>
                  </p>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-500">Line Number</label>
                  {/* CR-1154: both PO number and line deep-link to the line item. */}
                  <p className="mt-1 text-base">
                    <PoLineLink poLineId={grnData.po_line}>
                      {grnData.line_number || grnData.po_line_details?.line_number || '-'}
                    </PoLineLink>
                  </p>
                </div>

                {/* CR-1154: NCRs raised against this GRN, hyperlinked to each. */}
                {grnData.related_ncrs && grnData.related_ncrs.length > 0 && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Related NCRs</label>
                    <div className="mt-1 flex flex-wrap gap-2">
                      {grnData.related_ncrs.map((ncr) => (
                        <a
                          key={ncr.id}
                          href={`/quality/ncr/${ncr.id}/edit`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 rounded bg-red-50 px-2 py-0.5 text-sm text-red-700 hover:underline"
                        >
                          {ncr.ncr_number}
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {/* CR-7265: a schedule line is a concept only tenants on the
                    schedule-grain module have. Without it every delivery shows
                    "Schedule Line -", which reads as missing data rather than
                    as not applicable. The wrapper here was `{( ... )}` -- a
                    fragment with no condition in it at all, which looks like a
                    gate and is not one. */}
                {scheduleCollabActive && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Schedule Line</label>
                    <p className="mt-1 text-base">
                      {grnData.schedule_line_number != null && grnData.schedule_line_number !== ''
                        ? (grnData.schedule_line_number === '0' ? '#0 (historical)' : `#${grnData.schedule_line_number}`)
                        : '-'}
                    </p>
                  </div>
                )}

                <div>
                  <label className="text-sm font-medium text-gray-500">Part Number</label>
                  <p className="mt-1 text-base font-mono">{grnData.part_number || grnData.po_line_details?.part_number || '-'}</p>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-500">Part Description</label>
                  <p className="mt-1 text-base">{grnData.part_description || '-'}</p>
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-6">
                <div>
                  <label className="text-sm font-medium text-gray-500">Qty Ordered</label>
                  <p className="mt-1 text-base">{(() => {
                    if (scheduleCollabActive && grnData.schedule_qty != null) return formatQuantity(grnData.schedule_qty);
                    if (grnData.quantity_ordered != null) return formatQuantity(grnData.quantity_ordered);
                    if (grnData.po_line_details?.quantity != null) return formatQuantity(grnData.po_line_details.quantity);
                    return '-';
                  })()}</p>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-500">Qty Received</label>
                  <p className="mt-1 text-base">{formatQuantity(grnData.quantity_received)}</p>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-500">Delivery Date</label>
                  <p className="mt-1 text-base">{formatDate(grnData.receipt_date)}</p>
                </div>

                {/* CR-4607: MRP Date and ODD fields */}
                <div>
                  <label className="text-sm font-medium text-gray-500">MRP Date</label>
                  <p className="mt-1 text-base">{grnData.mrp_date ? formatDate(grnData.mrp_date) : '-'}</p>
                </div>

                {/* Customer review 11 Sep (item 36): ODD, On-Time Status and
                    Variance all come from ONE rule and ONE reference date —
                    the backend delivery-verdict engine (purchasing/services/
                    delivery_verdict.py): schedule slot -> line ODD (original
                    promise) -> delivery date. Before, the verdict was taken
                    against the line ODD while this panel measured Variance
                    against the re-planned delivery date, so LGR062375 read
                    Early next to "-1945 days" (vs 31/12/2031). The re-planned
                    date is still shown, as the Customer Required Date, when it differs.
                    Older payloads without verdict_anchor_date fall back to the
                    previous schedule_date -> delivery_date chain (CR-4607). */}
                {(() => {
                  const odd = grnData.verdict_anchor_date ?? grnData.schedule_date ?? grnData.original_delivery_date ?? grnData.po_delivery_date;
                  const currentDue = grnData.po_delivery_date;
                  const delivered = grnData.receipt_date;
                  const days = grnData.variance_days !== undefined
                    ? grnData.variance_days
                    : (odd && delivered ? Math.round((new Date(delivered).getTime() - new Date(odd).getTime()) / 86400000) : null);
                  return (
                    <>
                      <div>
                        <label className="text-sm font-medium text-gray-500" title="The date the On-Time Status is measured against">{ORIGINAL_REQUIRED_DATE}</label>
                        <p className="mt-1 text-base">{odd ? formatDate(odd) : '-'}</p>
                      </div>
                      {currentDue && odd && currentDue !== odd && (
                        <div>
                          <label className="text-sm font-medium text-gray-500" title="The PO line's latest (re-planned) required date — not used for On-Time">{CUSTOMER_REQUIRED_DATE}</label>
                          <p className="mt-1 text-base">{formatDate(currentDue)}</p>
                        </div>
                      )}
                      <div>
                        <label className="text-sm font-medium text-gray-500">On-Time Status</label>
                        <div className="mt-1">
                          {grnData.on_time_status ? getOntimeStatusBadge(grnData.on_time_status, grnData.otif_overridden || grnData.otif_acknowledged, grnData.on_time_status_display, grnData.allocations) : '-'}
                        </div>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500" title="Delivery date minus ODD; the wording follows the On-Time Status (supplier tolerance window applied)">Variance (Days)</label>
                        <div className="mt-1">{getVarianceBadge(days, grnData.on_time_status)}</div>
                      </div>
                    </>
                  );
                })()}

              </div>
            </div>


            {/* CR-4601 rework: GRN Workflow audit trail (Acknowledge / Dispute / Resolve) */}
            {(workflowLogs.length > 0 || grnData.workflow_status === 'acknowledged') && (
              <div className="mt-6 border-t pt-6">
                <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  Acknowledgment
                </h3>
                {/* Latest state summary card */}
                <div className="bg-green-50 rounded-lg p-4 space-y-2 text-sm mb-3">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">Current Status:</span>
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 capitalize">
                      {(grnData.workflow_status || 'pending_review').replace('_', ' ')}
                    </span>
                  </div>
                  {grnData.acknowledged_by_details && (
                    <div>
                      <span className="text-gray-500">Acknowledged By:</span>{' '}
                      <span className="font-medium">
                        {grnData.acknowledged_by_details.full_name || grnData.acknowledged_by_details.email}
                      </span>
                    </div>
                  )}
                  {grnData.acknowledged_date && (
                    <div>
                      <span className="text-gray-500">Acknowledged On:</span>{' '}
                      <span className="font-medium">{formatDate(grnData.acknowledged_date)}</span>
                    </div>
                  )}
                  {grnData.acknowledgment_comments && (
                    <div>
                      <span className="text-gray-500">Comments:</span>{' '}
                      <span>{grnData.acknowledgment_comments}</span>
                    </div>
                  )}
                </div>

                {/* CR-4661: History table — matches Appeal History column structure
                    (When, Company NAME, User, Description, Attachments) */}
                {workflowLogs.length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-gray-500 mb-2">Acknowledgment History</h4>
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full text-xs">
                        <thead className="bg-gray-50">
                          <tr>
                            <SortableTh field="change_date" sortField={workflowSort.sortField} sortDirection={workflowSort.sortDirection} onSort={workflowSort.onSort} align="left" className="text-left py-2 px-3 font-medium">When</SortableTh>
                            <SortableTh field="changed_by_company" sortField={workflowSort.sortField} sortDirection={workflowSort.sortDirection} onSort={workflowSort.onSort} align="left" className="text-left py-2 px-3 font-medium">Company</SortableTh>
                            <SortableTh field="changed_by_name" sortField={workflowSort.sortField} sortDirection={workflowSort.sortDirection} onSort={workflowSort.onSort} align="left" className="text-left py-2 px-3 font-medium">User</SortableTh>
                            <SortableTh field="description" sortField={workflowSort.sortField} sortDirection={workflowSort.sortDirection} onSort={workflowSort.onSort} align="left" className="text-left py-2 px-3 font-medium">Description</SortableTh>
                            {/* Not sortable: attachments are not a field on the log row. */}
                            <th className="text-left py-2 px-3 font-medium">Attachments</th>
                          </tr>
                        </thead>
                        <tbody>
                          {workflowSort.sortedRows.map((log) => {
                            const cleanedNote = cleanWorkflowNote(log.reason);
                            return (
                              <tr key={log.id} className="border-t">
                                <td className="py-2 px-3 text-gray-600">{formatDate(log.change_date)}</td>
                                <td className="py-2 px-3">
                                  {log.changed_by_company
                                    || log.changed_by_details?.companyName
                                    || (log.company_type ? log.company_type.charAt(0).toUpperCase() + log.company_type.slice(1) : '-')}
                                </td>
                                <td className="py-2 px-3">
                                  {log.changed_by_details?.full_name || log.changed_by_details?.email || 'System'}
                                </td>
                                <td className="py-2 px-3">
                                  <span className="font-medium">{describeWorkflowAction(log.new_value, log.old_value || '')}</span>
                                  {cleanedNote && <span className="text-gray-500"> — {cleanedNote}</span>}
                                </td>
                                <td className="py-2 px-3 text-gray-400">-</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* CR-4611: Restructured Latest Appeal Section */}
            {grnData.latest_appeal && latestAppeal && (
              <div className="mt-6 border-t pt-6">
                <h3 className="text-sm font-semibold mb-3">Latest Appeal (#{latestAppeal.id})</h3>
                <div className="bg-orange-50 rounded-lg p-4 space-y-2 text-sm">
                  {/* Status row with badge aligned right */}
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">Status:</span>
                    {getAppealStatusBadge(latestAppeal.status, latestAppeal.is_customer_appeal)}
                  </div>
                  {/* Qty Appealed: From X to Y — CR-4677: use snapshot */}
                  {latestAppeal.qty_claimed && (
                    <div>
                      <span className="text-gray-500">Qty Appealed:</span>{' '}
                      <span>From <span className="font-medium">{latestAppeal.original_qty || grnData.quantity_received}</span> to <span className="font-medium text-orange-700">{latestAppeal.qty_claimed}</span></span>
                    </div>
                  )}
                  {/* Delivery Date Appealed: From X to Y */}
                  {/* CR-4677: Use original_receipt_date (snapshot at appeal creation) so
                      the "from" value isn't overwritten after acceptance. Falls back to
                      grnData.receipt_date for appeals created before the snapshot field. */}
                  {latestAppeal.date_claimed && (
                    <div>
                      <span className="text-gray-500">Delivery Date Appealed:</span>{' '}
                      <span>From <span className="font-medium">{formatDate(latestAppeal.original_receipt_date || grnData.receipt_date)}</span> to <span className="font-medium text-orange-700">{formatDate(latestAppeal.date_claimed)}</span></span>
                    </div>
                  )}
                  {/* ODD Appealed: From X to Y */}
                  {latestAppeal.odd_claimed && (
                    <div>
                      <span className="text-gray-500">ODD Appealed:</span>{' '}
                      {/* "From" is the ODD this delivery was judged against
                          (verdict_anchor_date), not the line's re-planned
                          delivery date. */}
                      <span>From <span className="font-medium">{(grnData.verdict_anchor_date ?? grnData.po_delivery_date) ? formatDate((grnData.verdict_anchor_date ?? grnData.po_delivery_date) as string) : '-'}</span> to <span className="font-medium text-orange-700">{formatDate(latestAppeal.odd_claimed)}</span></span>
                    </div>
                  )}
                  {latestAppeal.customer_note && (
                    <div><span className="text-gray-500">Customer Note:</span> <span>{latestAppeal.customer_note}</span></div>
                  )}
                </div>

                {/* CR-4611: Appeal History table with company, user, description, attachments */}
                {appealLogs.length > 0 && (
                  <div className="mt-4">
                    <h4 className="text-xs font-semibold text-gray-500 mb-2">Appeal History</h4>
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full text-xs">
                        <thead className="bg-gray-50">
                          <tr>
                            <SortableTh field="performed_at" sortField={appealSort.sortField} sortDirection={appealSort.sortDirection} onSort={appealSort.onSort} align="left" className="text-left py-2 px-3 font-medium">When</SortableTh>
                            <SortableTh field="performed_by_company" sortField={appealSort.sortField} sortDirection={appealSort.sortDirection} onSort={appealSort.onSort} align="left" className="text-left py-2 px-3 font-medium">Company</SortableTh>
                            <SortableTh field="performed_by_name" sortField={appealSort.sortField} sortDirection={appealSort.sortDirection} onSort={appealSort.onSort} align="left" className="text-left py-2 px-3 font-medium">User</SortableTh>
                            <SortableTh field="description" sortField={appealSort.sortField} sortDirection={appealSort.sortDirection} onSort={appealSort.onSort} align="left" className="text-left py-2 px-3 font-medium">Description</SortableTh>
                            {/* Not sortable: attachments are not a field on the log row. */}
                            <th className="text-left py-2 px-3 font-medium">Attachments</th>
                          </tr>
                        </thead>
                        <tbody>
                          {appealSort.sortedRows.map((log) => {
                            // Show attachments only on the 'created' log entry
                            const showAttachments = log.action === 'created' && appealAttachments.length > 0;
                            return (
                              <tr key={log.id} className="border-t">
                                <td className="py-2 px-3 text-gray-600">{formatDate(log.performed_at)}</td>
                                <td className="py-2 px-3">{log.performed_by_company || '-'}</td>
                                <td className="py-2 px-3">{log.performed_by_name || 'System'}</td>
                                <td className="py-2 px-3">
                                  <span className="capitalize font-medium">{log.action}</span>
                                  {log.notes && <span className="text-gray-500"> — {log.notes}</span>}
                                </td>
                                <td className="py-2 px-3">
                                  {showAttachments ? (
                                    <div className="space-y-1">
                                      {appealAttachments.map((att) => (
                                        <button
                                          key={att.id}
                                          onClick={async () => {
                                            try {
                                              const res = await purchasingAPI.downloadAppealAttachment(latestAppeal!.id, att.id);
                                              const url = window.URL.createObjectURL(new Blob([res.data]));
                                              const link = document.createElement('a');
                                              link.href = url;
                                              link.download = att.file_name;
                                              link.click();
                                              window.URL.revokeObjectURL(url);
                                            } catch {
                                              toast.error('Failed to download attachment');
                                            }
                                          }}
                                          className="block text-blue-600 hover:underline text-left"
                                          title={att.file_name}
                                        >
                                          {att.file_name.length > 25 ? att.file_name.slice(0, 22) + '...' : att.file_name}
                                        </button>
                                      ))}
                                    </div>
                                  ) : (
                                    <span className="text-gray-400">-</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* GRN Score Chart.
              CR-5884: Suppliers can have this chart hidden via Admin Setup →
              Purchasing → Open Orders → Hide Trend Line. */}
          {grnData.po_line && !hideTrendLineForSupplier && (
            <div className="px-6">
              <GRNScoreChart
                poLineId={grnData.po_line}
                poNumber={grnData.po_number}
                lineNumber={grnData.line_number}
                // CR-7106: drill-down mutations refresh this modal AND the
                // outer deliveries list (handleAppealSuccess already does
                // fetchGRNDetails + onRefresh) — previously they refreshed
                // only the drill-down's own rows.
                onMutated={handleAppealSuccess}
              />
            </div>
          )}

            </TabsContent>
          </Tabs>
          </div>{/* End scrollable content */}

          {/* Footer */}
          <div className="flex justify-between items-center p-6 border-t shrink-0">
            <div className="flex gap-3">
              {/* Supplier View */}
              {/* CR-4600: Supplier view — only Acknowledge, no Raise Dispute.
                  CR-4869 round 2: gate the entire supplier action cluster on
                  !loading. The pop-up renders before the appeal-lookup API
                  call resolves; while latestAppeal is still null, the
                  Acknowledge branch below its gate evaluated true and the
                  button flashed green for a frame before flipping to Cancel
                  Appeal once latestAppeal loaded. Suppressing the cluster
                  during the loading window avoids that visual pop. */}
              {companyType === 'supplier' && !loading && initialLoaded && (
                <>
                  {/* CR-4869: Hide Acknowledge when there's any active
                      (awaiting_response / queried) appeal — the supplier
                      shouldn't be able to acknowledge a delivery while an
                      appeal of either side is still in flight. Acknowledge
                      returns once the appeal is resolved / cancelled. */}
                  {/* Early / Late only, as in the grid: an on-time row has nothing to
                      acknowledge, and acknowledging would close its quantity appeal. */}
                  {grnData.workflow_status === 'pending_review'
                    && (grnData.on_time_status === 'early' || grnData.on_time_status === 'late')
                    && !(latestAppeal
                      && ['awaiting_response', 'queried'].includes(latestAppeal.status)) && (
                    <button
                      onClick={() => setShowAcknowledgeModal(true)}
                      disabled={loading}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                    >
                      <CheckCircle className="h-4 w-4" />
                      Acknowledge
                    </button>
                  )}
                  {grnData.workflow_status === 'disputed' && (
                    <button
                      onClick={() => setShowResolveModal(true)}
                      disabled={loading}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                    >
                      <CheckCheck className="h-4 w-4" />
                      Resolve Dispute
                    </button>
                  )}
                  {/* CR-4601 rework: Appeal closed once GRN is acknowledged.
                      Also suppress when a customer-raised appeal is still
                      active — the supplier should respond to that, not
                      stack another appeal on top.
                      CR-5884: Suppliers can have these Appeal /
                      Update-Appeal / Cancel-Appeal controls hidden via
                      Admin Setup → Purchasing → Open Orders → Hide OTIF
                      Override Button.
                      CR-7319: appeal controls carry the gavel; the triangle
                      stays on the Disputed badge, which is a warning. */}
                  {!hideAppealForSupplier && grnData.can_appeal && grnData.workflow_status !== 'acknowledged'
                    && !(latestAppeal
                      && latestAppeal.is_customer_appeal
                      && ['awaiting_response', 'queried'].includes(latestAppeal.status)) && (
                    <button
                      onClick={() => setShowAppealModal(true)}
                      disabled={loading}
                      className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                    >
                      <Gavel className="h-4 w-4" />
                      Appeal
                    </button>
                  )}
                  {!hideAppealForSupplier && latestAppeal?.status === 'queried' && (
                    <button
                      onClick={() => setShowAppealModal(true)}
                      disabled={loading}
                      className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                    >
                      <Gavel className="h-4 w-4" />
                      Update Appeal
                    </button>
                  )}
                  {!hideAppealForSupplier && latestAppeal && !latestAppeal.is_customer_appeal && ['awaiting_response', 'queried'].includes(latestAppeal.status) && (
                    <button
                      onClick={async () => {
                        if (!window.confirm('Are you sure you want to cancel this appeal?')) return;
                        try {
                          setLoading(true);
                          await purchasingAPI.cancelDeliveryAppeal(latestAppeal.id);
                          toast.success('Appeal cancelled');
                          await handleAppealSuccess();
                        } catch (err: unknown) {
                          toast.error(getApiErrorMessage(err, 'Failed to cancel appeal'));
                        } finally {
                          setLoading(false);
                        }
                      }}
                      disabled={loading}
                      className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50 flex items-center gap-2"
                    >
                      <X className="h-4 w-4" />
                      Cancel Appeal
                    </button>
                  )}
                  {/* Customer-raised appeal awaiting supplier response →
                      supplier responds. Customer appeals used to auto-accept,
                      so the supplier never had a UI to act on them. */}
                  {latestAppeal && latestAppeal.is_customer_appeal && latestAppeal.status === 'awaiting_response' && (
                    <button
                      onClick={() => setShowAppealResponseModal(true)}
                      disabled={loading}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                    >
                      <CheckCircle className="h-4 w-4" />
                      Respond to Appeal
                    </button>
                  )}
                </>
              )}

              {/* Customer / Helix Admin View — CR-4649: Action logic based on
                  delivery status and appeal state:
                  - On Time + no appeal → no actions (truly nothing to do)
                  - Early/Late + no appeal → Acknowledge + Appeal
                  - Pending appeal → Respond to Appeal (Accept/Query/Reject via modal)
                  - Disputed → Resolve Dispute
                  CR-4649 round 2: previously we offered a bare Acknowledge
                  on On-Time pending_review rows; Jacqueline rejected that
                  because no action is required in that state. */}
              {/* CR-4869 round 2: same !loading gate on the customer side
                  so the Respond-to-Appeal / Acknowledge cluster doesn't
                  flash before the latestAppeal lookup resolves. */}
              {(companyType === 'customer' || companyType === 'helix') && !loading && initialLoaded && (() => {
                const isOnTime = grnData.on_time_status === 'on_time';
                // hasPendingAppeal = a *supplier-raised* appeal awaiting
                // customer response. Customer-raised appeals are responded
                // to by the supplier — those don't surface a Respond button
                // to the customer.
                const hasPendingAppeal = latestAppeal?.status === 'awaiting_response'
                  && !latestAppeal?.is_customer_appeal;
                // hasActiveAppeal = any awaiting/queried appeal regardless of
                // side. We use this to suppress the Acknowledge + Appeal
                // cluster: a customer with their own pending appeal is
                // already waiting on the supplier, so re-offering
                // Acknowledge / Appeal would be confusing.
                const hasActiveAppeal = latestAppeal
                  && ['awaiting_response', 'queried'].includes(latestAppeal.status);
                // CR-4739 round 2: once the previous appeal is resolved
                // (rejected / accepted) the row is closed to further
                // action — no Acknowledge, no Appeal. Before this guard
                // the pop-up kept offering those buttons on rejected
                // rows because workflow_status stayed at pending_review.
                // CR-4678 round 2: 'cancelled' must NOT gate the buttons
                // off — a cancelled appeal is the supplier withdrawing
                // their own submission, and a re-appeal should be
                // possible. Jacqueline's bounce: the listing table
                // correctly re-showed the Appeal button after cancel
                // (via CR-4678 round 1) but this pop-up pathway still
                // hid it because of the overly-broad gate.
                const hasResolvedAppeal = latestAppeal
                  && ['rejected', 'accepted'].includes(latestAppeal.status);
                const isPendingReview = grnData.workflow_status === 'pending_review';
                const isDisputed = grnData.workflow_status === 'disputed';
                const isEarlyOrLate = grnData.on_time_status === 'early' || grnData.on_time_status === 'late';
                // On time with the wrong quantity: Appeal (on quantity) only, no Acknowledge.
                const quantityOnly = isQuantityOnlyAppeal(grnData) && grnData.can_appeal === true;

                // CR-6517: an overridden GRN is resolved — no acknowledge /
                // appeal-response / dispute controls, regardless of appeal state.
                if (grnData.otif_overridden) {
                  return <span className="text-sm text-muted-foreground">Overridden — no action required.</span>;
                }

                return (
                  <>
                    {/* Pending appeal from supplier → respond */}
                    {hasPendingAppeal && (
                      <button
                        onClick={() => setShowAppealResponseModal(true)}
                        disabled={loading}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                      >
                        <CheckCircle className="h-4 w-4" />
                        Respond to Appeal
                      </button>
                    )}

                    {/* Early/Late + no active or resolved appeal → Acknowledge
                        + Appeal.
                        CR-4739 round 2: also suppress when a prior appeal
                        is rejected/accepted/cancelled — the row is done.
                        CR-6313: the Appeal button is back for the customer
                        side, reverting the 2026-07-08 retirement. Confirmed
                        by Sarah 2026-09-08: a customer captures an appeal on
                        the supplier's behalf and it is accepted on
                        submission, which is how the Supplier Portal has
                        always worked. `can_appeal` is about the GRN's own
                        state, not who is asking, so it gates both sides. */}
                    {!hasActiveAppeal && !hasResolvedAppeal && isPendingReview && (isEarlyOrLate || quantityOnly) && (
                      <>
                        {isEarlyOrLate && (
                          <button
                            onClick={() => setShowAcknowledgeModal(true)}
                            disabled={loading}
                            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                          >
                            <CheckCircle className="h-4 w-4" />
                            Acknowledge
                          </button>
                        )}
                        {grnData.can_appeal && (
                          <button
                            onClick={() => setShowAppealModal(true)}
                            disabled={loading}
                            className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                          >
                            <Gavel className="h-4 w-4" />
                            Appeal
                          </button>
                        )}
                      </>
                    )}

                    {/* CR-4649 round 2: On-Time + pending review intentionally
                        has no button — the delivery is fine, there's nothing
                        for the customer to act on. Keeping a bare Acknowledge
                        here (previous behaviour) confused reviewers into
                        thinking something was outstanding.
                        isOnTime check left only to silence unused-var lint. */}
                    {!hasPendingAppeal && isOnTime && false}

                    {/* Disputed → Resolve */}
                    {isDisputed && (
                      <button
                        onClick={() => setShowResolveModal(true)}
                        disabled={loading}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                      >
                        <CheckCheck className="h-4 w-4" />
                        Resolve Dispute
                      </button>
                    )}

                    {/* Customer cancel their own appeal */}
                    {latestAppeal && latestAppeal.is_customer_appeal && ['awaiting_response', 'queried'].includes(latestAppeal.status) && (
                      <button
                        onClick={async () => {
                          if (!window.confirm('Are you sure you want to cancel this appeal?')) return;
                          try {
                            setLoading(true);
                            await purchasingAPI.cancelDeliveryAppeal(latestAppeal.id);
                            toast.success('Appeal cancelled');
                            await handleAppealSuccess();
                          } catch (err: unknown) {
                            toast.error(getApiErrorMessage(err, 'Failed to cancel appeal'));
                          } finally {
                            setLoading(false);
                          }
                        }}
                        disabled={loading}
                        className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50 flex items-center gap-2"
                      >
                        <X className="h-4 w-4" />
                        Cancel Appeal
                      </button>
                    )}
                  </>
                );
              })()}
            </div>

            <button
              onClick={onClose}
              disabled={loading}
              className="px-6 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg font-medium transition-colors disabled:opacity-50"
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {/* GRN Workflow Modals */}
      <AcknowledgeGRNModal
        isOpen={showAcknowledgeModal}
        onClose={() => setShowAcknowledgeModal(false)}
        grnId={grnData.id}
        grnNumber={grnData.grn_number}
        partNumber={grnData.part_number || grnData.po_line_details?.part_number || '-'}
        poNumber={grnData.po_number || grnData.po_line_details?.po_details?.po_number || '-'}
        onSubmit={handleWorkflowSubmit}
      />

      <DisputeGRNModal
        isOpen={showDisputeModal}
        onClose={() => setShowDisputeModal(false)}
        grnId={grnData.id}
        grnNumber={grnData.grn_number}
        partNumber={grnData.part_number || grnData.po_line_details?.part_number || '-'}
        poNumber={grnData.po_number || grnData.po_line_details?.po_details?.po_number || '-'}
        onSubmit={handleWorkflowSubmit}
      />

      <ResolveGRNDisputeModal
        isOpen={showResolveModal}
        onClose={() => setShowResolveModal(false)}
        grnId={grnData.id}
        grnNumber={grnData.grn_number}
        partNumber={grnData.part_number || grnData.po_line_details?.part_number || '-'}
        poNumber={grnData.po_number || grnData.po_line_details?.po_details?.po_number || '-'}
        onSubmit={handleWorkflowSubmit}
      />

      {/* Appeal Modals */}
      <RaiseAppealModal
        isOpen={showAppealModal}
        onClose={() => setShowAppealModal(false)}
        grn={grnData}
        onSuccess={handleAppealSuccess}
        existingAppealId={latestAppeal?.status === 'queried' ? latestAppeal.id : undefined}
      />

      {latestAppeal && (
        <AppealResponseModal
          isOpen={showAppealResponseModal}
          onClose={() => setShowAppealResponseModal(false)}
          appeal={latestAppeal}
          onSuccess={handleAppealSuccess}
        />
      )}
    </>
  );
}
